import React, { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { getCurrentUser } from '../services/api'
import {
  PlusIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  BriefcaseIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

// API functions
const fetchManagedTenants = async (token: string) => {
  const response = await fetch('/api/msp/managed-tenants?include_stats=true', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch managed tenants')
  return response.json()
}

const fetchMSPDashboard = async (token: string) => {
  const response = await fetch('/api/msp/dashboard', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch dashboard')
  return response.json()
}

const addManagedTenant = async ({
  token,
  tenantNameOrDomain,
  accessScope,
}: {
  token: string
  tenantNameOrDomain: string
  accessScope?: string[]
}) => {
  const response = await fetch('/api/msp/managed-tenants', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ domain_or_name: tenantNameOrDomain, access_scope: accessScope }),
  })
  if (!response.ok) {
    let errorMessage = 'Failed to add tenant'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail[0].msg || error.detail[0].message || JSON.stringify(error.detail[0])
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

const createManagedTenant = async ({
  token,
  name,
  displayName,
  adminEmail,
  adminFullName,
}: {
  token: string
  name: string
  displayName?: string
  adminEmail?: string
  adminFullName?: string
}) => {
  const response = await fetch('/api/msp/tenants', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      display_name: displayName || name,
      admin_email: adminEmail || undefined,
      admin_full_name: adminFullName || undefined,
    }),
  })
  if (!response.ok) {
    let errorMessage = 'Failed to create tenant'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail[0].msg || error.detail[0].message || JSON.stringify(error.detail[0])
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

const removeManagedTenant = async ({
  token,
  relationshipId,
}: {
  token: string
  relationshipId: string
}) => {
  const response = await fetch(`/api/msp/managed-tenants/${relationshipId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    let errorMessage = 'Failed to remove tenant'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail[0].msg || error.detail[0].message || JSON.stringify(error.detail[0])
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  // 204 No Content has no body
  if (response.status === 204) return {}
  return response.json()
}

interface ManagedTenant {
  id: string  // tenant id
  name: string
  display_name: string
  type: string
  status: string
  domain: string | null
  subscription_tier: string | null
  max_users: number | null
  max_apps: number | null
  relationship: {
    id: string
    status: string
    created_at: string
    access_scope: string[] | null
  }
  created_at: string | null
  stats?: {
    user_count: number
    jobs_last_30_days: number
  }
}

interface DashboardStats {
  managed_tenant_count: number
  total_users_across_tenants: number
  total_jobs_last_30_days: number
}

const TenantsSettings: React.FC = () => {
  const { token, tenantContext, updateAccessibleTenants } = useAuthStore()
  const queryClient = useQueryClient()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<ManagedTenant | null>(null)
  const [addMode, setAddMode] = useState<'existing' | 'new'>('new')
  const [newTenantName, setNewTenantName] = useState('')
  const [newTenantDisplayName, setNewTenantDisplayName] = useState('')
  const [newTenantAdminEmail, setNewTenantAdminEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [tenantChangeWarning, setTenantChangeWarning] = useState<string | null>(null)

  const isMSP = tenantContext?.tenant_type === 'msp'

  // Refresh the accessible tenants list in auth store (for tenant switcher)
  const refreshAccessibleTenants = useCallback(async () => {
    try {
      const me = await getCurrentUser()
      updateAccessibleTenants(me.accessible_tenants)
    } catch (err) {
      console.error('Failed to refresh accessible tenants:', err)
    }
  }, [updateAccessibleTenants])

  // Fetch managed tenants
  const { data: tenants, isLoading: tenantsLoading } = useQuery({
    queryKey: ['managed-tenants'],
    queryFn: () => fetchManagedTenants(token!),
    enabled: !!token && isMSP,
  })

  // Fetch dashboard stats
  const { data: dashboard } = useQuery<DashboardStats>({
    queryKey: ['msp-dashboard'],
    queryFn: () => fetchMSPDashboard(token!),
    enabled: !!token && isMSP,
  })

  // Add existing tenant mutation
  const addMutation = useMutation({
    mutationFn: addManagedTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-tenants'] })
      queryClient.invalidateQueries({ queryKey: ['msp-dashboard'] })
      refreshAccessibleTenants()
      closeAddModal()
      setTenantChangeWarning('Tenant added successfully. Please re-login for the tenant switcher dropdown to reflect this change.')
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  // Create new tenant mutation
  const createMutation = useMutation({
    mutationFn: createManagedTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-tenants'] })
      queryClient.invalidateQueries({ queryKey: ['msp-dashboard'] })
      refreshAccessibleTenants()
      closeAddModal()
      setTenantChangeWarning('Tenant created successfully. Please re-login for the tenant switcher dropdown to reflect this change.')
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const closeAddModal = () => {
    setIsAddModalOpen(false)
    setNewTenantName('')
    setNewTenantDisplayName('')
    setNewTenantAdminEmail('')
    setAddMode('new')
    setError(null)
  }

  // Remove tenant mutation
  const removeMutation = useMutation({
    mutationFn: removeManagedTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-tenants'] })
      queryClient.invalidateQueries({ queryKey: ['msp-dashboard'] })
      refreshAccessibleTenants()
      setIsRemoveModalOpen(false)
      setSelectedTenant(null)
      setTenantChangeWarning('Tenant removed successfully. The tenant switcher has been updated.')
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const handleAddTenant = () => {
    const name = newTenantName.trim()
    if (!name) {
      setError('Please enter a tenant name')
      return
    }

    // Validate ASCII only
    if (!/^[\x20-\x7E]+$/.test(name)) {
      setError('Tenant name must contain only ASCII characters (a-z, 0-9, hyphens)')
      return
    }

    if (addMode === 'new') {
      createMutation.mutate({
        token: token!,
        name,
        adminEmail: newTenantAdminEmail.trim() || undefined,
      })
    } else {
      addMutation.mutate({ token: token!, tenantNameOrDomain: name })
    }
  }

  const handleRemoveTenant = () => {
    if (selectedTenant) {
      // Use the tenant id for removal, backend expects tenant_id not relationship_id
      removeMutation.mutate({ token: token!, relationshipId: selectedTenant.id })
    }
  }

  if (!isMSP) {
    return (
      <div className="p-8">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Tenant Management</h2>
            <p className="text-theme-text-secondary">
              Manage your client tenants and their access.
            </p>
          </div>

          <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
            <div className="text-center py-8">
              <BuildingOfficeIcon className="w-12 h-12 mx-auto text-theme-text-tertiary mb-4" />
              <p className="text-theme-text-tertiary">
                Tenant management is only available for MSP accounts.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Tenant Management</h2>
            <p className="text-theme-text-secondary">
              Manage your client tenants and their access to your MSP services.
            </p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add Tenant
          </button>
        </div>

      {/* Tenant change warning */}
      {tenantChangeWarning && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-800 dark:text-amber-300">{tenantChangeWarning}</p>
          </div>
          <button
            onClick={() => setTenantChangeWarning(null)}
            className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <BuildingOfficeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-theme-text-primary">
                  {dashboard.managed_tenant_count}
                </p>
                <p className="text-sm text-theme-text-secondary">Managed Tenants</p>
              </div>
            </div>
          </div>
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-theme-text-primary">
                  {dashboard.total_users_across_tenants}
                </p>
                <p className="text-sm text-theme-text-secondary">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <BriefcaseIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-theme-text-primary">
                  {dashboard.total_jobs_last_30_days}
                </p>
                <p className="text-sm text-theme-text-secondary">Jobs (30 days)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenants List */}
      <div className="bg-theme-card border border-theme-border-primary rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-theme-border-primary">
          <h3 className="text-lg font-semibold text-theme-text-primary">Managed Tenants</h3>
        </div>

        {tenantsLoading ? (
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-theme-tertiary rounded-lg" />
              ))}
            </div>
          </div>
        ) : tenants?.length > 0 ? (
          <div className="divide-y divide-theme-border-primary">
            {tenants.map((tenant: ManagedTenant) => (
              <div
                key={tenant.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-theme-secondary transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-theme-tertiary rounded-lg flex items-center justify-center">
                    <BuildingOfficeIcon className="w-5 h-5 text-theme-text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-theme-text-primary">
                      {tenant.display_name || tenant.name}
                    </p>
                    <p className="text-sm text-theme-text-tertiary">{tenant.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-theme-text-primary">
                      {tenant.stats?.user_count || 0} users
                    </p>
                    <p className="text-xs text-theme-text-tertiary">
                      {tenant.stats?.jobs_last_30_days || 0} jobs (30d)
                    </p>
                  </div>

                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      tenant.status === 'active'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {tenant.status}
                  </span>

                  <button
                    onClick={() => {
                      setSelectedTenant(tenant)
                      setIsRemoveModalOpen(true)
                    }}
                    className="p-2 text-theme-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Remove tenant"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <BuildingOfficeIcon className="w-12 h-12 mx-auto text-theme-text-tertiary mb-4" />
            <p className="text-theme-text-secondary mb-4">No managed tenants yet</p>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="text-blue-500 hover:text-blue-400"
            >
              Add your first tenant
            </button>
          </div>
        )}
      </div>

      {/* Add Tenant Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={closeAddModal}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">
                  {addMode === 'new' ? 'Create New Tenant' : 'Add Existing Tenant'}
                </h2>
                <button
                  onClick={closeAddModal}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Mode Toggle */}
                <div className="flex rounded-lg bg-theme-secondary p-1">
                  <button
                    onClick={() => { setAddMode('new'); setError(null); }}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                      addMode === 'new'
                        ? 'bg-blue-600 text-white'
                        : 'text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                  >
                    Create New
                  </button>
                  <button
                    onClick={() => { setAddMode('existing'); setError(null); }}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                      addMode === 'existing'
                        ? 'bg-blue-600 text-white'
                        : 'text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                  >
                    Add Existing
                  </button>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {addMode === 'new' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-theme-text-primary mb-2">
                        Tenant Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenantName}
                        onChange={(e) => {
                          const val = e.target.value
                          setNewTenantName(val)
                          if (val && !/^[\x20-\x7E]*$/.test(val)) {
                            setError('Tenant name must contain only ASCII characters (a-z, 0-9, hyphens)')
                          } else if (error?.includes('ASCII')) {
                            setError(null)
                          }
                        }}
                        placeholder="e.g., acme-corp"
                        className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-theme-text-tertiary">
                        Unique identifier (lowercase, no spaces, ASCII only)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-theme-text-primary mb-2">
                        Admin Email
                      </label>
                      <input
                        type="email"
                        value={newTenantAdminEmail}
                        onChange={(e) => setNewTenantAdminEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-theme-text-tertiary">
                        Optional: Creates an admin user for this tenant
                      </p>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-theme-text-primary mb-2">
                      Tenant Name
                    </label>
                    <input
                      type="text"
                      value={newTenantName}
                      onChange={(e) => setNewTenantName(e.target.value)}
                      placeholder="Enter existing tenant name"
                      className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-theme-text-tertiary">
                      The tenant must already exist in the system
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={closeAddModal}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTenant}
                  disabled={addMutation.isPending || createMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
                >
                  {(addMutation.isPending || createMutation.isPending)
                    ? (addMode === 'new' ? 'Creating...' : 'Adding...')
                    : (addMode === 'new' ? 'Create Tenant' : 'Add Tenant')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Tenant Modal */}
      {isRemoveModalOpen && selectedTenant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsRemoveModalOpen(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">Remove Tenant</h2>
                <button
                  onClick={() => setIsRemoveModalOpen(false)}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <p className="text-center text-theme-text-primary mb-2">
                  Are you sure you want to remove{' '}
                  <span className="font-semibold">
                    {selectedTenant.display_name || selectedTenant.name}
                  </span>
                  ?
                </p>
                <p className="text-center text-theme-text-secondary text-sm">
                  This will revoke your MSP access to this tenant. The tenant and its data will not
                  be deleted.
                </p>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={() => setIsRemoveModalOpen(false)}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveTenant}
                  disabled={removeMutation.isPending}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
                >
                  {removeMutation.isPending ? 'Removing...' : 'Remove Tenant'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default TenantsSettings
